import React, { useState } from 'react'
import {
  XMarkIcon,
  CreditCardIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { paymentGatewayAPI } from '@/services/api'

interface CreateCreditCardOrderModalProps {
  isOpen: boolean
  onClose: () => void
  gatewayId: string
  onSuccess?: () => void
}

interface OrderResult {
  order_id: string
  status: string
  amount: number
  installments: number
}

const CreateCreditCardOrderModal: React.FC<CreateCreditCardOrderModalProps> = ({
  isOpen,
  onClose,
  gatewayId,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [orderResult, setOrderResult] = useState<OrderResult | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    // Customer data
    customer_name: '',
    customer_email: '',
    customer_document: '',
    customer_type: 'individual',
    customer_phone_area: '',
    customer_phone_number: '',
    customer_address_line: '',
    customer_address_zip: '',
    customer_address_city: '',
    customer_address_state: '',

    // Item data
    item_description: '',
    item_amount: '', // Em reais
    item_quantity: '1',

    // Card data
    card_number: '',
    card_holder_name: '',
    card_exp_month: '',
    card_exp_year: '',
    card_cvv: '',
    installments: '1',
    statement_descriptor: '',
    capture: true
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)

      // Converter valor de reais para centavos
      const amountInCents = Math.round(parseFloat(formData.item_amount) * 100)
      const quantity = parseInt(formData.item_quantity)

      // Preparar dados do pedido
      const orderData = {
        amount: amountInCents * quantity, // Total em centavos
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_document: formData.customer_document.replace(/\D/g, ''),
        customer_type: formData.customer_type,
        customer_phone_country: '55',
        customer_phone_area: formData.customer_phone_area,
        customer_phone_number: formData.customer_phone_number,
        customer_address_line: formData.customer_address_line,
        customer_address_zip: formData.customer_address_zip.replace(/\D/g, ''),
        customer_address_city: formData.customer_address_city,
        customer_address_state: formData.customer_address_state,
        customer_address_country: 'BR',
        items: [
          {
            description: formData.item_description,
            amount: amountInCents,
            quantity: quantity
          }
        ],
        // Card data
        card_number: formData.card_number.replace(/\s/g, ''),
        card_holder_name: formData.card_holder_name,
        card_exp_month: formData.card_exp_month,
        card_exp_year: formData.card_exp_year,
        card_cvv: formData.card_cvv,
        installments: parseInt(formData.installments),
        statement_descriptor: formData.statement_descriptor || undefined,
        capture: formData.capture
      }

      console.log('📤 Enviando pedido Cartão:', orderData)

      const result = await paymentGatewayAPI.createOrderCreditCard(gatewayId, orderData)

      console.log('✅ Pedido Cartão criado:', result)

      setOrderResult(result)

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar pedido Cartão:', error)
      alert('Erro ao criar pedido: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  const handleClose = () => {
    setOrderResult(null)
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_document: '',
      customer_type: 'individual',
      customer_phone_area: '',
      customer_phone_number: '',
      customer_address_line: '',
      customer_address_zip: '',
      customer_address_city: '',
      customer_address_state: '',
      item_description: '',
      item_amount: '',
      item_quantity: '1',
      card_number: '',
      card_holder_name: '',
      card_exp_month: '',
      card_exp_year: '',
      card_cvv: '',
      installments: '1',
      statement_descriptor: '',
      capture: true
    })
    onClose()
  }

  if (!isOpen) return null

  // Se já temos resultado, mostra confirmação
  if (orderResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Pedido Criado!</h2>
                  <p className="text-sm text-gray-500">ID: {orderResult.order_id}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-green-700 uppercase">{orderResult.status}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Valor:</span>
                <span className="font-semibold text-gray-900">{formatCurrency(orderResult.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Parcelas:</span>
                <span className="font-semibold text-gray-900">{orderResult.installments}x</span>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Form para criar pedido
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Criar Pedido - Cartão de Crédito</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Dados do Cliente */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados do Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF/CNPJ *
                </label>
                <input
                  type="text"
                  name="customer_document"
                  value={formData.customer_document}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DDD *
                </label>
                <input
                  type="text"
                  name="customer_phone_area"
                  value={formData.customer_phone_area}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone *
                </label>
                <input
                  type="text"
                  name="customer_phone_number"
                  value={formData.customer_phone_number}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço *
                </label>
                <input
                  type="text"
                  name="customer_address_line"
                  value={formData.customer_address_line}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CEP *
                </label>
                <input
                  type="text"
                  name="customer_address_zip"
                  value={formData.customer_address_zip}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cidade *
                </label>
                <input
                  type="text"
                  name="customer_address_city"
                  value={formData.customer_address_city}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado *
                </label>
                <input
                  type="text"
                  name="customer_address_state"
                  value={formData.customer_address_state}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Dados do Item */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Item / Produto</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  name="item_description"
                  value={formData.item_description}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Unitário (R$) *
                </label>
                <input
                  type="number"
                  name="item_amount"
                  value={formData.item_amount}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade *
                </label>
                <input
                  type="number"
                  name="item_quantity"
                  value={formData.item_quantity}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total
                </label>
                <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg font-semibold text-gray-900">
                  {formData.item_amount && formData.item_quantity
                    ? `R$ ${(parseFloat(formData.item_amount) * parseInt(formData.item_quantity)).toFixed(2)}`
                    : 'R$ 0,00'}
                </div>
              </div>
            </div>
          </div>

          {/* Dados do Cartão */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados do Cartão</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número do Cartão *
                </label>
                <input
                  type="text"
                  name="card_number"
                  value={formData.card_number}
                  onChange={handleChange}
                  required
                  placeholder="0000 0000 0000 0000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome no Cartão *
                </label>
                <input
                  type="text"
                  name="card_holder_name"
                  value={formData.card_holder_name}
                  onChange={handleChange}
                  required
                  placeholder="NOME COMO NO CARTÃO"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mês Validade *
                </label>
                <input
                  type="text"
                  name="card_exp_month"
                  value={formData.card_exp_month}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  placeholder="12"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ano Validade *
                </label>
                <input
                  type="text"
                  name="card_exp_year"
                  value={formData.card_exp_year}
                  onChange={handleChange}
                  required
                  maxLength={4}
                  placeholder="2025"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CVV *
                </label>
                <input
                  type="text"
                  name="card_cvv"
                  value={formData.card_cvv}
                  onChange={handleChange}
                  required
                  maxLength={4}
                  placeholder="123"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parcelas *
                </label>
                <select
                  name="installments"
                  value={formData.installments}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                    <option key={n} value={n}>
                      {n}x {formData.item_amount && formData.item_quantity
                        ? `de R$ ${((parseFloat(formData.item_amount) * parseInt(formData.item_quantity)) / n).toFixed(2)}`
                        : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processando...
                </>
              ) : (
                <>
                  <CreditCardIcon className="w-5 h-5" />
                  Processar Pagamento
                </>
              )}
            </button>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-xs text-yellow-800">
              ⚠️ <strong>Ambiente de Testes:</strong> Use cartões de teste do Pagar.me. Nunca envie dados reais de cartão em sandbox.
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateCreditCardOrderModal
